import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
  icon: string;
}

const faqItems: FAQItem[] = [
  {
    question: "Is my information and our sessions confidential?",
    answer: "Yes. Everything shared in our sessions — spoken or drawn — stays completely private. Online sessions are not recorded.",
    icon: "ri-lock-line"
  },
  {
    question: "What if I don't feel comfortable after my first session?",
    answer: "That's completely okay, and it happens. If you don't feel it's the right fit after your first session — no connection, no sense that this is working for you — I'll refund your payment for that session, no questions asked. Finding the right fit matters more than continuing out of obligation.",
    icon: "ri-heart-line"
  },
  {
    question: "What platform do you use for online sessions?",
    answer: "Online sessions take place over Google Meet. You'll receive a link after booking — no app download required, it opens directly in your browser.",
    icon: "ri-vidicon-line"
  },
  {
    question: "What if I need to cancel or reschedule my session?",
    answer: "You can cancel or reschedule free of charge up to 24 hours before your session. Cancellations made less than 24 hours in advance are non-refundable, as this time is reserved specifically for you.",
    icon: "ri-calendar-check-line"
  },
  {
    question: "What happens to my drawing from the \"Try for Free\" offering — is it published anywhere?",
    answer: "Your drawing is completely confidential. It's used only to prepare your personal written reflection and is never shared or published anywhere.",
    icon: "ri-shield-check-line"
  },
  {
    question: "Do I need any artistic skill or experience?",
    answer: "Not at all. Art therapy isn't about creating something beautiful or technically skilled — it's about using creative expression as a way to explore feelings that can be hard to put into words. No prior art experience is needed.",
    icon: "ri-palette-line"
  }
];

const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqItems.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": item.answer
    }
  }))
};

export default function FAQSection() {
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set());
  const [showAllMobile, setShowAllMobile] = useState(false);

  const toggleQuestion = (index: number) => {
    setExpandedIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      <section id="faq" className="pt-8 pb-6 lg:pb-12 bg-[#81998C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-4">
            <h2
              className="text-4xl lg:text-5xl font-normal cinematic-text-dark mb-4"
            >
              Frequently Asked Questions
            </h2>
            <p
              className="text-lg md:text-xl text-[#385B47]/80 max-w-3xl mx-auto leading-snug md:leading-relaxed"
            >
              Common questions about art therapy sessions, confidentiality, and how it all works
            </p>
          </div>

          {/* FAQ Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-5">
            {faqItems.map((item, index) => {
              const isExpanded = expandedIndices.has(index);
              const isHiddenOnMobile = index >= 3 && !showAllMobile;
              return (
                <div
                  key={index}
                  className={`bg-[#4E6D70] rounded-2xl shadow-lg border border-[#3D5C4A] overflow-hidden transition-all duration-300 ${isHiddenOnMobile ? 'hidden md:block' : ''}`}
                >
                  <button
                    onClick={() => toggleQuestion(index)}
                    className="w-full flex flex-col items-start gap-0 px-6 pt-6 pb-3 cursor-pointer text-left group"
                    aria-expanded={isExpanded}
                  >
                    {/* Icon */}
                    <div className="w-16 h-16 lg:w-12 lg:h-12 cinematic-accent-bg rounded-full flex items-center justify-center mb-6 lg:mb-3">
                      <i className={`${item.icon} text-2xl lg:text-xl text-white`}></i>
                    </div>

                    {/* Question heading */}
                    <span className="text-xl lg:text-lg font-normal cinematic-text-light pr-8 relative w-full">
                      {item.question}
                      {/* Chevron */}
                      <span className="absolute right-0 top-0 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-[#385B47] group-hover:bg-[#2F4C3B] transition-colors">
                        <i
                          className={`ri-arrow-down-s-line text-lg text-white transition-transform duration-300 ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                        ></i>
                      </span>
                    </span>
                  </button>

                  <div
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${
                      isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-6 pb-5 pt-0">
                      <div className="border-t border-[#3D5C4A] pt-4">
                        <p className="text-sm lg:text-base cinematic-text-light-secondary leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {!showAllMobile && (
            <div className="text-center mt-6 md:hidden">
              <button
                onClick={() => setShowAllMobile(true)}
                className="inline-flex items-center gap-2 text-sm font-normal underline cursor-pointer transition-opacity duration-300 hover:opacity-75 whitespace-nowrap"
                style={{ color: '#385B47' }}
              >
                Show all questions
                <i className="ri-arrow-down-line"></i>
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}